/**
 * Spin up a sandbox test location for an approved integrator request (AES-277).
 *
 * Calls portal sandbox APIs (AES-278): resolve workspace → POST 202 → poll GET.
 * Do not call Glofox Signup / integrators from the browser.
 */

import {
  presentApiError,
  SANDBOX_ERROR_MESSAGES,
  TEST_LOCATION_FAILURE,
  TEST_LOCATION_STILL_PROVISIONING,
} from "@/lib/api/portal-error-copy";
import {
  createSandboxDemoLocation,
  findWorkspaceForAccessRequest,
  getSandboxDemoLocation,
  listSandboxWorkspaces,
  type DemoLocation,
  type StudioType,
} from "@/lib/api/glofox/sandbox-workspaces";

export type TestLocationProvisionStatus = "pending" | "success" | "error";

export type SpinUpTestLocationInput = {
  accessRequestId: string | number;
  locationName: string;
  adminPassword: string;
  studioType: StudioType;
};

export type SpinUpTestLocationResult = {
  id: string;
  name: string;
  status: TestLocationProvisionStatus;
  errorMessage?: string;
  /** Stable API code when provisioning failed over HTTP. */
  errorCode?: string;
  /** Waiting copy when status is pending. Not a failure. */
  statusMessage?: string;
  /** Glofox location id when provisioning succeeded. */
  branchId?: string;
  /** Studio admin password when provisioning succeeded (owner GET). */
  adminPassword?: string;
};

export type SpinUpPollOptions = {
  intervalMs?: number;
  maxAttempts?: number;
  sleep?: (ms: number) => Promise<void>;
};

const DEFAULT_POLL_INTERVAL_MS = 1500;
const DEFAULT_MAX_ATTEMPTS = 80;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

function mapDemoLocationToResult(location: DemoLocation): SpinUpTestLocationResult {
  const branchId = location.branch_id?.trim() || undefined;
  const withBranch = branchId ? { branchId } : {};

  if (location.status === "ready") {
    return {
      id: location.id,
      name: location.display_name,
      status: "success",
      adminPassword: location.admin_password ?? undefined,
      ...withBranch,
    };
  }

  return {
    id: location.id,
    name: location.display_name,
    status: "error",
    errorMessage: location.failure_reason?.trim() || TEST_LOCATION_FAILURE,
    ...withBranch,
  };
}

function spinUpFailure(err: unknown): {
  errorMessage: string;
  errorCode?: string;
} {
  const presented = presentApiError(err, {
    generic: TEST_LOCATION_FAILURE,
    byCode: SANDBOX_ERROR_MESSAGES,
  });
  return presented.code
    ? { errorMessage: presented.message, errorCode: presented.code }
    : { errorMessage: presented.message };
}

/**
 * Provisions a sandbox test location via the portal sandbox API.
 */
export async function spinUpTestLocation(
  input: SpinUpTestLocationInput,
  pollOptions: SpinUpPollOptions = {},
): Promise<SpinUpTestLocationResult> {
  const name = input.locationName.trim();
  const accessRequestId = String(input.accessRequestId);
  const intervalMs = pollOptions.intervalMs ?? DEFAULT_POLL_INTERVAL_MS;
  const maxAttempts = pollOptions.maxAttempts ?? DEFAULT_MAX_ATTEMPTS;
  const sleep = pollOptions.sleep ?? delay;

  let workspaces;
  try {
    ({ workspaces } = await listSandboxWorkspaces());
  } catch (err) {
    return {
      id: `pending_${accessRequestId}`,
      name,
      status: "error",
      ...spinUpFailure(err),
    };
  }

  const workspace = findWorkspaceForAccessRequest(workspaces, accessRequestId);
  if (!workspace) {
    return {
      id: `pending_${accessRequestId}`,
      name,
      status: "error",
      errorMessage: SANDBOX_ERROR_MESSAGES["sandbox.workspace_not_found"],
      errorCode: "sandbox.workspace_not_found",
    };
  }

  let createResponse;
  try {
    createResponse = await createSandboxDemoLocation(workspace.id, {
      admin_password: input.adminPassword,
      studio_type: input.studioType,
    });
  } catch (err) {
    return {
      id: `pending_${accessRequestId}`,
      name,
      status: "error",
      ...spinUpFailure(err),
    };
  }

  const locationId = createResponse.id;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    if (attempt > 0) {
      await sleep(intervalMs);
    }

    let location: DemoLocation;
    try {
      location = await getSandboxDemoLocation(workspace.id, locationId);
    } catch (err) {
      if (attempt === maxAttempts - 1) {
        return {
          id: locationId,
          name,
          status: "error",
          ...spinUpFailure(err),
        };
      }
      continue;
    }

    if (location.status === "ready" || location.status === "failed") {
      return mapDemoLocationToResult(location);
    }
  }

  // Still creating on the server — keep pending so the UI does not treat
  // timeout as a hard failure (and does not unlock another create).
  return {
    id: locationId,
    name,
    status: "pending",
    statusMessage: TEST_LOCATION_STILL_PROVISIONING,
  };
}
